export const downloadFileAtUrl = async (url: string, title: string, callback?: () => void) => {
	try {
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = title;

		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);

		if (callback) {
			callback();
		}
	} catch (error) {
		throw error;
	}
};

export function downloadBlobAsFile(fileName: string = 'download', fileBlob: Blob, callback?: () => void): void {
	const finalFilename = buildSafeFilename(fileName);

	// @ts-ignore
	if (typeof window.navigator !== 'undefined' && window.navigator.msSaveOrOpenBlob) {
		// @ts-ignore
		window.navigator.msSaveOrOpenBlob(fileBlob, finalFilename);
		return;
	}

	const temporaryObjectUrl = URL.createObjectURL(fileBlob);
	const anchorElement = document.createElement('a');
	anchorElement.href = temporaryObjectUrl;
	anchorElement.download = finalFilename;
	anchorElement.rel = 'noopener';
	anchorElement.style.display = 'none';
	document.body.appendChild(anchorElement);
	anchorElement.click();
	anchorElement.remove();
	URL.revokeObjectURL(temporaryObjectUrl);

	if (callback) {
		callback();
	}
}

export function downloadHtmlStringAsTxtFile(fileName: string = 'document', htmlString: string): void {
	const plainTextContent = convertHtmlStringToPlainText(htmlString).trim();
	const utf8ByteOrderMark = new Uint8Array([0xef, 0xbb, 0xbf]);
	const textFileBlob = new Blob([utf8ByteOrderMark, plainTextContent], { type: 'text/plain;charset=utf-8' });
	const safeSlugifiedFilename = slugifyFilename(fileName);
	const finalFilename = `${safeSlugifiedFilename}.txt`;

	// @ts-ignore
	if (typeof window.navigator !== 'undefined' && window.navigator.msSaveOrOpenBlob) {
		// @ts-ignore
		window.navigator.msSaveOrOpenBlob(textFileBlob, finalFilename);
		return;
	}

	const temporaryObjectUrl = URL.createObjectURL(textFileBlob);
	const anchorElement = document.createElement('a');
	anchorElement.href = temporaryObjectUrl;
	anchorElement.download = finalFilename;
	anchorElement.rel = 'noopener';
	anchorElement.style.display = 'none';
	document.body.appendChild(anchorElement);
	anchorElement.click();
	anchorElement.remove();
	URL.revokeObjectURL(temporaryObjectUrl);
}

function convertHtmlStringToPlainText(htmlString: string): string {
	const domParser = new DOMParser();
	const parsedDocument = domParser.parseFromString(htmlString, 'text/html');

	parsedDocument.querySelectorAll('script, style, noscript').forEach((element) => element.remove());

	const blockLevelTagNames = new Set([
		'ADDRESS',
		'ARTICLE',
		'ASIDE',
		'BLOCKQUOTE',
		'CANVAS',
		'DD',
		'DIV',
		'DL',
		'DT',
		'FIELDSET',
		'FIGCAPTION',
		'FIGURE',
		'FOOTER',
		'FORM',
		'H1',
		'H2',
		'H3',
		'H4',
		'H5',
		'H6',
		'HEADER',
		'HR',
		'LI',
		'MAIN',
		'NAV',
		'NOSCRIPT',
		'OL',
		'P',
		'PRE',
		'SECTION',
		'TABLE',
		'TFOOT',
		'UL',
	]);

	const textPieces: string[] = [];

	const traverseNode = (currentNode: Node) => {
		if (currentNode.nodeType === Node.TEXT_NODE) {
			const normalizedText = (currentNode.nodeValue ?? '').replace(/\s+/g, ' ');
			if (normalizedText.trim().length) textPieces.push(normalizedText);
			return;
		}

		if (currentNode.nodeType !== Node.ELEMENT_NODE) return;

		const currentElement = currentNode as HTMLElement;
		const currentTagName = currentElement.tagName;

		if (currentTagName === 'BR') {
			textPieces.push('\n');
			return;
		}

		const isBlockLevelElement = blockLevelTagNames.has(currentTagName);
		if (isBlockLevelElement) textPieces.push('\n');

		if (currentTagName === 'LI') textPieces.push('- ');

		for (const childNode of Array.from(currentElement.childNodes)) {
			traverseNode(childNode);
		}

		if (isBlockLevelElement) textPieces.push('\n');
	};

	traverseNode(parsedDocument.body);

	let plainText = textPieces.join('');
	const textAreaElement = document.createElement('textarea');
	textAreaElement.innerHTML = plainText;
	plainText = textAreaElement.value;
	plainText = plainText.replace(/\n{3,}/g, '\n\n');
	plainText = plainText
		.split('\n')
		.map((line) => line.replace(/[ \t]+$/g, '').replace(/^[ \t]+/g, ''))
		.join('\n');

	return plainText;
}

function buildSafeFilename(rawFilename: string): string {
	const trimmedFilename = rawFilename.trim();
	const extensionMatch = trimmedFilename.match(/(\.[a-z0-9]{1,10})$/i);
	const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
	const baseFilename = extension ? trimmedFilename.slice(0, -extension.length) : trimmedFilename;
	const safeBaseFilename = slugifyFilename(baseFilename) || 'download';

	return `${safeBaseFilename}${extension}`;
}

function slugifyFilename(rawFilename: string): string {
	const accentsAndDiacrictics = /[\u0300-\u036f]/g;
	const nonAlphaNumeric = /[^a-z0-9]+/g;
	const leadingAndTrailingDashes = /^-+|-+$/g;
	const collapseDashes = /-{2,}/g;

	return rawFilename
		.toLowerCase()
		.normalize('NFKD')
		.replace(accentsAndDiacrictics, '')
		.replace(nonAlphaNumeric, '-')
		.replace(leadingAndTrailingDashes, '')
		.replace(collapseDashes, '-');
}
